import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Team } from 'modules/teams/domain/interfaces/team';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import {
  createCreateTeamReducer,
  createDeleteTeamReducer,
  createEditTeamReducer,
  createGetTournamentTeamsByFilterReducer,
  createGetTournamentTeamsReducer,
} from 'modules/teams/store/teams/reducers';

export interface TeamsSliceState {
  isLoading: boolean;
  isLoaded: boolean;
  error?: any;

  processing: boolean;
  processedName?: string;

  teams: Team[];
  limit: number;
  allDataLoaded: boolean;

  filter: {
    text: string;
    teams: Team[];
    allDataLoaded: boolean;
  };

  tournamentId?: number;
}

const initialState: TeamsSliceState = {
  isLoading: false,
  isLoaded: false,

  processing: false,

  teams: [],
  limit: 20,
  allDataLoaded: false,

  filter: {
    text: '',
    teams: [],
    allDataLoaded: false,
  },
};

export const teamsSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = undefined;
    },
    reset(state, action: PayloadAction<{ tournamentId: number }>) {
      state.teams = [];
      state.tournamentId = action.payload.tournamentId;
      state.allDataLoaded = false;
      state.filter = { text: '', teams: [], allDataLoaded: false };
    },
    setFilter: (state, action: PayloadAction<{ text: string }>) => {
      state.filter.text = action.payload.text;
    },
    removePlayerFromTeam: (state, action: PayloadAction<{ teamId: number; userId: number }>) => {
      const { teamId, userId } = action.payload;
      const team = state.teams.find((team) => team.id === teamId);
      if (team) {
        team.players = team.players.filter((player) => player.userId !== userId);
      }
    },
    addPlayerToTeam: (state, action: PayloadAction<{ teamId: number; player: TeamPlayer }>) => {
      const { teamId, player } = action.payload;
      const team = state.teams.find((team) => team.id === teamId);
      if (team) {
        team.players.push(player);
      }
    },
  },
  extraReducers: (builder) => {
    createGetTournamentTeamsReducer(builder);
    createGetTournamentTeamsByFilterReducer(builder);
    createCreateTeamReducer(builder);
    createEditTeamReducer(builder);
    createDeleteTeamReducer(builder);
  },
});

export const { resetError, reset, setFilter, removePlayerFromTeam, addPlayerToTeam } = teamsSlice.actions;

export default teamsSlice.reducer;
